import { ADMIN_TABLE_PAGE_SIZE, sanitiseSearch } from "@/features/admin/tables";
import { supabaseClient } from "@/lib/supabase";
import type { TemplateSettingsSchemaT } from "@patient-watch/types";
import { toast } from "@patient-watch/ui/sonner";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export const ADMIN_TEMPLATE_LINK_STATUSES = ["master", "copy", "standalone"] as const;
export type AdminTemplateLinkStatus = (typeof ADMIN_TEMPLATE_LINK_STATUSES)[number];

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isUuid(value: string | null | undefined): value is string {
    return Boolean(value && UUID_PATTERN.test(value.trim()));
}

function toUuid(hex: string): string {
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/**
 * A partial template ID (such as the short ID shown in tables) as a UUID range,
 * since PostgREST cannot ILIKE a uuid column. Null when the text is not a hex prefix.
 */
export function templateIdPrefixRange(value: string): { from: string; to: string } | null {
    const hex = value.trim().toLowerCase().replace(/-/g, "");
    if (hex.length < 4 || hex.length > 32 || !/^[0-9a-f]+$/.test(hex)) return null;
    return { from: toUuid(hex.padEnd(32, "0")), to: toUuid(hex.padEnd(32, "f")) };
}

export interface AdminTemplateFilters {
    q?: string;
    ownerId?: string;
    masterId?: string;
    linkStatus?: AdminTemplateLinkStatus;
    isPublic?: boolean;
    /** Owned by, or shared via templates_access with, any of these users. */
    clinicianIds?: string[];
    labels?: string[];
    organisationIds?: string[];
    formIds?: string[];
    eventTypes?: string[];
    /** Only templates that are part of this study. */
    studyId?: string;
    page?: number;
    pageSize?: number;
}

export interface AdminTemplateOwner {
    user_id: string;
    name: string | null;
    email: string | null;
}

/** Just enough of a template to render its clinical title. */
export interface AdminTemplateSummary {
    template_id: string;
    user_id: string | null;
    settings: TemplateSettingsSchemaT;
    quickstart_key: string | null;
    duplicated_from_template_id: string | null;
}

export interface AdminTemplateStudy {
    study_id: string;
    study_name: string;
    is_published: boolean;
}

export interface AdminTemplateRow extends AdminTemplateSummary {
    is_public: boolean;
    created_at: string | null;
    updated_at: string | null;
    owner: AdminTemplateOwner | null;
    master: AdminTemplateSummary | null;
    linked_copy_count: number;
    studies: AdminTemplateStudy[];
}

export interface AdminTemplatesResult {
    templates: AdminTemplateRow[];
    totalCount: number;
    page: number;
    pageSize: number;
}

/** PostgREST quoted value, so JSON with quotes, commas or brackets survives inside or=(...). */
function quoteFilterValue(value: string): string {
    return `"${value.replace(/\\/g, "\\\\").replace(/"/g, '\\"')}"`;
}

/** One `settings` jsonb containment condition per value, ORed together. */
function settingsContainsAny(path: string, values: unknown[]): string {
    return values.map((value) => `settings->${path}.cs.${quoteFilterValue(JSON.stringify([value]))}`).join(",");
}

async function templateIdsSharedWith(userIds: string[]): Promise<string[]> {
    const { data, error } = await supabaseClient
        .from("templates_access")
        .select("template_id")
        .in("user_id", userIds)
        .limit(10000);
    if (error) throw error;
    return [...new Set((data ?? []).map((row) => row.template_id).filter((id): id is string => Boolean(id)))];
}

const SUMMARY_COLUMNS = "template_id, user_id, settings, quickstart_key, duplicated_from_template_id";

function toSummary(row: Record<string, unknown>): AdminTemplateSummary {
    return {
        template_id: row.template_id as string,
        user_id: (row.user_id as string | null) ?? null,
        settings: (row.settings ?? {}) as TemplateSettingsSchemaT,
        quickstart_key: (row.quickstart_key as string | null) ?? null,
        duplicated_from_template_id: (row.duplicated_from_template_id as string | null) ?? null,
    };
}

/** Linked copy count per master template, across every owner. */
async function fetchLinkedCopyCounts(): Promise<Map<string, number>> {
    const { data, error } = await supabaseClient
        .from("templates")
        .select("duplicated_from_template_id")
        .not("duplicated_from_template_id", "is", null)
        .limit(10000);
    if (error) throw error;
    const counts = new Map<string, number>();
    for (const row of data ?? []) {
        const id = row.duplicated_from_template_id;
        if (id) counts.set(id, (counts.get(id) ?? 0) + 1);
    }
    return counts;
}

async function templateIdsInStudy(studyId: string): Promise<string[]> {
    const { data, error } = await supabaseClient.from("study_templates").select("template_id").eq("study_id", studyId);
    if (error) throw error;
    return (data ?? []).map((row) => row.template_id);
}

/** Studies each template belongs to, for one page of templates. */
async function fetchTemplateStudies(templateIds: string[]): Promise<Map<string, AdminTemplateStudy[]>> {
    const byTemplate = new Map<string, AdminTemplateStudy[]>();
    if (templateIds.length === 0) return byTemplate;
    const { data, error } = await supabaseClient
        .from("study_templates")
        .select("template_id, studies(study_id, study_name, is_published)")
        .in("template_id", templateIds);
    if (error) throw error;
    for (const row of data ?? []) {
        const study = row.studies as AdminTemplateStudy | null;
        if (!study) continue;
        byTemplate.set(row.template_id, [...(byTemplate.get(row.template_id) ?? []), study]);
    }
    for (const studies of byTemplate.values()) studies.sort((a, b) => a.study_name.localeCompare(b.study_name));
    return byTemplate;
}

async function fetchOwners(userIds: string[]): Promise<Map<string, AdminTemplateOwner>> {
    const owners = new Map<string, AdminTemplateOwner>();
    if (userIds.length === 0) return owners;
    const { data, error } = await supabaseClient.from("profiles").select("user_id, name, email").in("user_id", userIds);
    if (error) throw error;
    for (const row of data ?? []) {
        if (row.user_id) owners.set(row.user_id, { user_id: row.user_id, name: row.name, email: row.email });
    }
    return owners;
}

async function userIdsMatchingOwnerSearch(q: string): Promise<string[]> {
    const { data, error } = await supabaseClient
        .from("profiles")
        .select("user_id")
        .or(`name.ilike.%${q}%,email.ilike.%${q}%`)
        .limit(200);
    if (error) throw error;
    return (data ?? []).map((row) => row.user_id).filter((id): id is string => Boolean(id));
}

export async function fetchAdminTemplates(filters: AdminTemplateFilters): Promise<AdminTemplatesResult> {
    const page = Math.max(1, filters.page ?? 1);
    const pageSize = Math.max(1, filters.pageSize ?? ADMIN_TABLE_PAGE_SIZE);
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    const copyCounts = await fetchLinkedCopyCounts();
    const masterIds = [...copyCounts.keys()];

    if (filters.linkStatus === "master" && masterIds.length === 0) {
        return { templates: [], totalCount: 0, page, pageSize };
    }

    const studyTemplateIds = filters.studyId ? await templateIdsInStudy(filters.studyId) : null;
    if (studyTemplateIds && studyTemplateIds.length === 0) {
        return { templates: [], totalCount: 0, page, pageSize };
    }

    let query = supabaseClient
        .from("templates")
        .select(`${SUMMARY_COLUMNS}, is_public, created_at, updated_at`, { count: "exact" });

    if (filters.ownerId) query = query.eq("user_id", filters.ownerId);
    if (studyTemplateIds) query = query.in("template_id", studyTemplateIds);
    if (filters.masterId) query = query.eq("duplicated_from_template_id", filters.masterId);
    if (filters.isPublic !== undefined) query = query.eq("is_public", filters.isPublic);
    if (filters.organisationIds?.length) query = query.in("settings->>organisation_id", filters.organisationIds);

    // Each entry is an OR group; groups are ANDed in one `or` param so they do not overwrite each other.
    const orGroups: string[] = [];
    if (filters.clinicianIds?.length) {
        const sharedIds = await templateIdsSharedWith(filters.clinicianIds);
        orGroups.push(
            [
                `user_id.in.(${filters.clinicianIds.join(",")})`,
                ...(sharedIds.length ? [`template_id.in.(${sharedIds.join(",")})`] : []),
            ].join(",")
        );
    }
    if (filters.labels?.length) orGroups.push(settingsContainsAny("diary_labels", filters.labels));
    if (filters.eventTypes?.length) orGroups.push(settingsContainsAny("required_events", filters.eventTypes));
    if (filters.formIds?.length) {
        orGroups.push(
            settingsContainsAny(
                "trackers",
                filters.formIds.map((form_id) => ({ form_id }))
            )
        );
    }

    if (filters.linkStatus === "copy") {
        query = query.not("duplicated_from_template_id", "is", null);
    } else if (filters.linkStatus === "master") {
        query = query.in("template_id", masterIds);
    } else if (filters.linkStatus === "standalone") {
        query = query.is("duplicated_from_template_id", null);
        if (masterIds.length > 0) query = query.not("template_id", "in", `(${masterIds.join(",")})`);
    }

    const rawQ = filters.q?.trim() ?? "";
    if (isUuid(rawQ)) {
        const id = rawQ.toLowerCase();
        orGroups.push(`template_id.eq.${id},duplicated_from_template_id.eq.${id},user_id.eq.${id}`);
    } else if (rawQ) {
        const q = sanitiseSearch(rawQ);
        const ownerIds = await userIdsMatchingOwnerSearch(q);
        const idRange = templateIdPrefixRange(rawQ);
        orGroups.push(
            [
                `settings->>name.ilike.%${q}%`,
                ...(ownerIds.length ? [`user_id.in.(${ownerIds.join(",")})`] : []),
                ...(idRange ? [`and(template_id.gte.${idRange.from},template_id.lte.${idRange.to})`] : []),
            ].join(",")
        );
    }

    const [onlyGroup] = orGroups;
    if (orGroups.length === 1 && onlyGroup) {
        query = query.or(onlyGroup);
    } else if (orGroups.length > 1) {
        query = query.or(`and(${orGroups.map((group) => `or(${group})`).join(",")})`);
    }

    const { data, error, count } = await query
        .order("updated_at", { ascending: false, nullsFirst: false })
        .range(from, to);
    if (error) throw error;

    const rows = data ?? [];
    const pageMasterIds = [
        ...new Set(rows.map((row) => row.duplicated_from_template_id).filter((id): id is string => Boolean(id))),
    ];

    const [mastersResult, owners, studies] = await Promise.all([
        pageMasterIds.length > 0
            ? supabaseClient.from("templates").select(SUMMARY_COLUMNS).in("template_id", pageMasterIds)
            : Promise.resolve({ data: [], error: null }),
        fetchOwners([...new Set(rows.map((row) => row.user_id).filter((id): id is string => Boolean(id)))]),
        fetchTemplateStudies(rows.map((row) => row.template_id as string)),
    ]);
    if (mastersResult.error) throw mastersResult.error;
    const masters = new Map(
        (mastersResult.data ?? []).map((row) => [row.template_id as string, toSummary(row)] as const)
    );

    const templates: AdminTemplateRow[] = rows.map((row) => {
        const summary = toSummary(row);
        return {
            ...summary,
            is_public: Boolean(row.is_public),
            created_at: row.created_at ?? null,
            updated_at: row.updated_at ?? null,
            owner: summary.user_id ? (owners.get(summary.user_id) ?? null) : null,
            master: summary.duplicated_from_template_id
                ? (masters.get(summary.duplicated_from_template_id) ?? null)
                : null,
            linked_copy_count: copyCounts.get(summary.template_id) ?? 0,
            studies: studies.get(summary.template_id) ?? [],
        };
    });

    return { templates, totalCount: count ?? templates.length, page, pageSize };
}

export function useAdminTemplates(filters: AdminTemplateFilters) {
    return useQuery({
        queryKey: ["admin-templates", filters],
        queryFn: () => fetchAdminTemplates(filters),
        staleTime: 1000 * 30,
        placeholderData: (previous) => previous,
    });
}

export interface AdminTemplateLookup extends AdminTemplateSummary {
    owner: AdminTemplateOwner | null;
    linked_copy_count: number;
}

/** Resolve a pasted template ID for the change master dialog. */
export function useAdminTemplateLookup(templateId: string | null) {
    return useQuery({
        queryKey: ["admin-template-lookup", templateId],
        enabled: isUuid(templateId),
        queryFn: async (): Promise<AdminTemplateLookup | null> => {
            const id = (templateId as string).trim().toLowerCase();
            const { data, error } = await supabaseClient
                .from("templates")
                .select(SUMMARY_COLUMNS)
                .eq("template_id", id)
                .maybeSingle();
            if (error) throw error;
            if (!data) return null;
            const summary = toSummary(data);
            const [owners, copies] = await Promise.all([
                fetchOwners(summary.user_id ? [summary.user_id] : []),
                supabaseClient
                    .from("templates")
                    .select("template_id", { count: "exact", head: true })
                    .eq("duplicated_from_template_id", id),
            ]);
            if (copies.error) throw copies.error;
            return {
                ...summary,
                owner: summary.user_id ? (owners.get(summary.user_id) ?? null) : null,
                linked_copy_count: copies.count ?? 0,
            };
        },
    });
}

/** Admin only: set any template's master by ID, or pass null to unlink. */
export function useAdminLinkTemplateToMaster() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            templateId,
            masterTemplateId,
        }: {
            templateId: string;
            masterTemplateId: string | null;
        }) => {
            const { error } = await supabaseClient.rpc("admin_link_template_to_master", {
                p_template_id: templateId,
                p_master_template_id: masterTemplateId,
            });
            if (error) throw error;
        },
        onSuccess: (_data, { masterTemplateId }) => {
            void queryClient.invalidateQueries({ queryKey: ["admin-templates"] });
            void queryClient.invalidateQueries({ queryKey: ["admin-template-lookup"] });
            void queryClient.invalidateQueries({ queryKey: ["templates"] });
            void queryClient.invalidateQueries({ queryKey: ["all-templates-admin"] });
            toast.success(masterTemplateId ? "Master template updated" : "Template unlinked from master");
        },
        onError: (error) => {
            toast.error(error instanceof Error ? error.message : "Could not change the master template");
        },
    });
}
